/**
 * Pins, as the review numbers them and says where they are (session 49,
 * `.docs/49-mockup-pins-and-guests.md` §Pins; threads since session 55,
 * `.docs/55-mockup-comments.md`). The review, the portal pages' summaries and the list all use
 * these, so a thread is "3" on the page, in the list and on the portal page alike.
 */

import type { MockupCommentView, MockupPin } from '@fareast/shared'

import type { PreviewWidth } from '../../components/portal/Files.js'
import type { NumberedPin } from '../../pins/usePinBridge.js'

/**
 * The version's pinned threads, numbered 1, 2, 3 in the order they were started. A resolved one
 * keeps its number — "see 4" means the same thread after 3 is resolved — and a hidden one has
 * none, in the studio either, so "pin 3" is the same thread on every side of the link.
 */
export function numberedPins(threads: readonly MockupCommentView[]): {
  readonly pins: readonly NumberedPin[]
  readonly numberOf: ReadonlyMap<string, number>
} {
  const pins: NumberedPin[] = []
  const numberOf = new Map<string, number>()
  for (const thread of threads) {
    if (thread.pin === null || thread.hidden_at !== null) continue
    const n = pins.length + 1
    pins.push({ n, pin: thread.pin, resolved: thread.resolved_at !== null })
    numberOf.set(thread.id, n)
  }
  return { pins, numberOf }
}

/**
 * A thread still to be dealt with: not resolved, and not hidden by the studio — the server's
 * `open_comments` (`service.ts` `openThreadCount`), said once on this side. A hidden one is
 * with the resolved ones, where the studio, the only side that sees it, finds it marked.
 */
export function isOpenThread(
  thread: Pick<MockupCommentView, 'resolved_at' | 'hidden_at'>,
): boolean {
  return thread.resolved_at === null && thread.hidden_at === null
}

/**
 * Which of the review's three widths a page that wide is seen at: a phone's frame is 390 pixels
 * and a tablet's 834, so a page up to 600 wide was a phone's, up to 1,023 a tablet's.
 */
export function widthFor(viewportW: number): PreviewWidth {
  if (viewportW <= 600) return 'phone'
  return viewportW < 1_024 ? 'tablet' : 'desktop'
}

/** Where a thread is, said in its row when it is not simply here on the page. */
export type ThreadWhere =
  | { readonly kind: 'here' }
  /** About the whole version, with no pin. */
  | { readonly kind: 'version' }
  /** On another page of the version. */
  | { readonly kind: 'page'; readonly page: string }
  /**
   * On this page, but not on it at this width — a desktop menu at a phone's. `width` is the one
   * it was left at, when that is another than the one shown.
   */
  | { readonly kind: 'hidden'; readonly width: PreviewWidth | null }

export function whereOf(
  thread: Pick<MockupCommentView, 'pin'>,
  n: number | undefined,
  looking: {
    /** The page the frame shows, or null before it has said. */
    readonly page: string | null
    /** The pins it could draw there. */
    readonly shown: ReadonlySet<number>
    readonly width: PreviewWidth
  },
): ThreadWhere {
  const pin: MockupPin | null = thread.pin
  if (pin === null) return { kind: 'version' }
  if (looking.page === null || n === undefined) return { kind: 'here' }
  if (pin.page !== looking.page) return { kind: 'page', page: pin.page }
  if (looking.shown.has(n)) return { kind: 'here' }
  const left = pin.viewport_w === undefined ? null : widthFor(pin.viewport_w)
  return { kind: 'hidden', width: left === looking.width ? null : left }
}

/** A comment's first words, for a row — one line of it, cut at a word near `length`. */
export function excerptOf(text: string, length = 140): string {
  const line = text.replace(/\s+/g, ' ').trim()
  if (line.length <= length) return line
  const cut = line.slice(0, length)
  const space = cut.lastIndexOf(' ')
  return `${(space > length / 2 ? cut.slice(0, space) : cut).trimEnd()}…`
}
